import { Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { tokenService } from '../../../services/tokenService';
import { useAppSelector } from '../../../store/hooks';
import AuthLoadingScreen from '../components/AuthLoadingScreen';

// Handles redirect back from Stytch Hosted Login (or other IdP) and exchanges
// the returned session_jwt for a first-party JWT via identity server.
//
// Expected URL params (depending on your Stytch Hosted Login configuration):
// - session_jwt: string (preferred)
// - or intermediate_token / other provider params (not handled here)
//
// Configure Hosted Login to redirect to: <app_origin>/auth/callback
export default function AuthCallback() {
  const nav = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [awaitingDriveRetry, setAwaitingDriveRetry] = useState(false);
  const authToken = useAppSelector((s) => s.auth.token);

  useEffect(() => {
    const run = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const sessionJwt = params.get('session_jwt') || '';
        if (!sessionJwt) {
          setError('Missing session_jwt in callback');
          return;
        }
        const token = await tokenService.exchangeStytchSession(sessionJwt);
        if (!token) {
          const prompt = tokenService.peekDrivePrompt();
          if (prompt) {
            setAwaitingDriveRetry(true);
            setError(null);
            return;
          }
          setError('Token exchange failed');
          return;
        }
        // Clean up URL and go home (or to intended route)
        nav('/', { replace: true });
      } catch (e: any) {
        setError(e?.message || 'Authentication failed');
      }
    };
    run();
  }, [nav]);

  useEffect(() => {
    if (!awaitingDriveRetry) return;
    if (!authToken) return;
    setAwaitingDriveRetry(false);
    nav('/', { replace: true });
  }, [awaitingDriveRetry, authToken, nav]);

  if (!error) {
    return <AuthLoadingScreen testId="auth-callback-loading-screen" />;
  }

  return (
    <AuthLoadingScreen showSpinner={false}>
      <Typography color="error" sx={{ bgcolor: 'white', borderRadius: 2, p: 3 }}>
        {error}
      </Typography>
    </AuthLoadingScreen>
  );
}
